/**
 * The end of a run: MISSION COMPLETE or MISSION FAILED, with the contract as it
 * finally stood and a way back out.
 *
 * The world keeps running underneath — on failure you're already a free-flying
 * spectator, and on success you're standing in the lobby — so this is an overlay
 * over a live game rather than a state the sim knows about. REDEPLOY replays the
 * exact same mission (same seed, same building, same job); the title screen is a
 * reload, matching how the rest of the app returns to the menu.
 */

import { visibleTasks, type Questline } from '../sim/quest';
import { formatMoney, type Score } from '../sim/score';

export interface MissionEndActions {
  onRedeploy: () => void;
  onTitle: () => void;
}

export class MissionEndScreen {
  private readonly root: HTMLElement;
  private shown = false;
  /** Verdict dismissed so the player can look at the level (see `haunt`). */
  private haunting = false;

  constructor(host: HTMLElement, private readonly actions: MissionEndActions) {
    this.root = document.createElement('div');
    this.root.className = 'mission-end';
    this.root.style.display = 'none';
    host.appendChild(this.root);
  }

  isShown(): boolean {
    return this.shown;
  }

  /** Show the outcome. Idempotent — the first outcome to land is the one that
   *  stands, so a death during the exfil frame can't overwrite a win. */
  show(outcome: 'complete' | 'failed', q: Questline | null, subtitle: string, score?: Score): void {
    if (this.shown) return;
    this.shown = true;
    const won = outcome === 'complete';
    const rows = (q ? visibleTasks(q) : [])
      .map((t) => `<div class="me-task me-task--${t.done ? 'done' : 'missed'}">`
        + `<span class="me-task-mark">${t.done ? '✓' : '✗'}</span>`
        + `<span>${escapeHtml(t.title)}</span></div>`)
      .join('');
    this.root.innerHTML = `
      <div class="me-panel me-panel--${won ? 'win' : 'lose'}">
        <div class="me-title">${won ? 'MISSION COMPLETE' : 'MISSION FAILED'}</div>
        <div class="me-sub">${escapeHtml(subtitle)}</div>
        <div class="me-tasks">${rows}</div>
        ${score ? this.payout(score) : ''}
        <button class="me-btn" data-act="redeploy" type="button">▸ REDEPLOY · same contract</button>
        ${won ? '' : '<button class="me-btn me-btn--ghost" data-act="haunt" type="button">◈ HAUNT INSTEAD</button>'}
        <button class="me-btn" data-act="title" type="button">◂ MAIN MENU</button>
      </div>`;
    this.root.style.display = '';
    this.root.querySelector('[data-act="redeploy"]')?.addEventListener('click', () => this.actions.onRedeploy());
    this.root.querySelector('[data-act="title"]')?.addEventListener('click', () => this.actions.onTitle());
    // HAUNT: get the panel out of the way and go on free-flying the level. The
    // run is still over — this just stops the verdict covering the building.
    this.root.querySelector('[data-act="haunt"]')?.addEventListener('click', () => this.haunt());
  }

  /** Hide the verdict and let the player roam. `reopen()` brings it back. */
  haunt(): void {
    this.root.style.display = 'none';
    this.haunting = true;
  }

  isHaunting(): boolean {
    return this.haunting;
  }

  /** Put the verdict back up (the ghost pressing Esc). */
  reopen(): void {
    if (!this.shown || !this.haunting) return;
    this.haunting = false;
    this.root.style.display = '';
  }

  /** The take, then what the job's manner was worth on top of it. */
  private payout(score: Score): string {
    const lines = [
      `<div class="me-pay-row"><span>Take</span><span>${formatMoney(score.take)}</span></div>`,
      ...score.bonuses.map((b) =>
        `<div class="me-pay-row me-pay-row--bonus"><span>${escapeHtml(b.label)}</span>`
        + `<span>+${formatMoney(b.amount)}</span></div>`),
      `<div class="me-pay-row me-pay-row--total"><span>Payout</span><span>${formatMoney(score.total)}</span></div>`,
    ];
    return `<div class="me-pay">${lines.join('')}</div>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}
